import { createAction } from 'store/utils';

import {
  AccountFilter,
  GenerateReconciliationDataResponse,
  ReconciliationGenerateParams,
  ReconciliationHeaderData,
  ReconDataLog,
  ReconciliationQueryParams,
  ReconciliationQueryResponse,
  SaveReconciliationReportParams,
  TransactionTypeFilter,
  ReconciliationViewParams,
  TaxRateFilter,
  ViewReconciliationDataResponse,
} from './models';
import {
  GENERATE_RECONCILIATION_REQUEST,
  GENERATE_RECONCILIATION_PAGE_SUCCESS,
  GENERATE_RECONCILIATION_COMPLETE,
  GENERATE_RECONCILIATION_FAILURE,
  GENERATE_RECONCILIATION_IN_PROGRESS,
  GENERATE_RECONCILIATION_RETRY_AFTER,
  QUERY_RECONCILIATION_REQUEST,
  QUERY_RECONCILIATION_SUCCESS,
  QUERY_RECONCILIATION_FAILURE,
  LOAD_RECONCILIATION_HEADER_REQUEST,
  LOAD_RECONCILIATION_HEADER_SUCCESS,
  LOAD_RECONCILIATION_HEADER_FAILURE,
  LOAD_RECONCILIATION_TRANSACTIONS_REQUEST,
  LOAD_RECONCILIATION_TRANSACTIONS_SUCCESS,
  LOAD_RECONCILIATION_TRANSACTIONS_FAILURE,
  VIEW_RECONCILIATION_REPORT_REQUEST,
  VIEW_RECONCILIATION_REPORT_PAGE_SUCCESS,
  VIEW_RECONCILIATION_REPORT_COMPLETE,
  VIEW_RECONCILIATION_REPORT_FAILURE,
  FETCH_RECONCILIATION_FILTERS_REQUEST,
  FETCH_RECONCILIATION_FILTERS_SUCCESS,
  FETCH_RECONCILIATION_FILTERS_FAILURE,
  FETCH_RECONCILIATION_LOGS_REQUEST,
  FETCH_RECONCILIATION_LOGS_SUCCESS,
  FETCH_RECONCILIATION_LOGS_FAILURE,
  SAVE_RECONCILIATION_REPORT_REQUEST,
  SAVE_RECONCILIATION_REPORT_SUCCESS,
  SAVE_RECONCILIATION_REPORT_FAILURE,
  RESET_RECONCILIATION,
  UPDATE_ORGANISATION_TAX_BALANCE_REQUEST,
  UPDATE_ORGANISATION_TAX_BALANCE_SUCCESS,
  UPDATE_ORGANISATION_TAX_BALANCE_FAILURE,
} from './constants';
import {
  GenerateReconciliationRequestAction,
  GenerateReconciliationPageSuccessAction,
  GenerateReconciliationCompleteAction,
  GenerateReconciliationFailureAction,
  GenerateReconciliationInProgressAction,
  GenerateReconciliationRetryAfterAction,
  QueryReconciliationRequestAction,
  QueryReconciliationSuccessAction,
  QueryReconciliationFailureAction,
  LoadReconciliationHeaderRequestAction,
  LoadReconciliationHeaderSuccessAction,
  LoadReconciliationHeaderFailureAction,
  LoadReconciliationTransactionsRequestAction,
  LoadReconciliationTransactionsSuccessAction,
  LoadReconciliationTransactionsFailureAction,
  ViewReconciliationReportRequestAction,
  ViewReconciliationReportPageSuccessAction,
  ViewReconciliationReportCompleteAction,
  ViewReconciliationReportFailureAction,
  FetchReconciliationFiltersRequestAction,
  FetchReconciliationFiltersSuccessAction,
  FetchReconciliationFiltersFailureAction,
  FetchReconciliationLogsRequestAction,
  FetchReconciliationLogsSuccessAction,
  FetchReconciliationLogsFailureAction,
  SaveReconciliationReportRequestAction,
  SaveReconciliationReportSuccessAction,
  SaveReconciliationReportFailureAction,
  ResetReconciliationAction,
  UpdateOrganisationTaxBalanceRequestAction,
  UpdateOrganisationTaxBalanceSuccessAction,
  UpdateOrganisationTaxBalanceFailureAction,
} from './types';

// ── Generate Reconciliation ────────────────────────────────────────────────

export const generateReconciliationRequest = (
  params: ReconciliationGenerateParams
): GenerateReconciliationRequestAction =>
  createAction(GENERATE_RECONCILIATION_REQUEST, params);

export const generateReconciliationPageSuccess = (
  data: GenerateReconciliationDataResponse
): GenerateReconciliationPageSuccessAction =>
  createAction(GENERATE_RECONCILIATION_PAGE_SUCCESS, data);

export const generateReconciliationComplete =
  (): GenerateReconciliationCompleteAction =>
    createAction(GENERATE_RECONCILIATION_COMPLETE);

export const generateReconciliationFailure =
  (): GenerateReconciliationFailureAction =>
    createAction(GENERATE_RECONCILIATION_FAILURE);

export const generateReconciliationInProgress =
  (): GenerateReconciliationInProgressAction =>
    createAction(GENERATE_RECONCILIATION_IN_PROGRESS);

export const generateReconciliationRetryAfter = (
  retryAfterSeconds: number
): GenerateReconciliationRetryAfterAction =>
  createAction(GENERATE_RECONCILIATION_RETRY_AFTER, { retryAfterSeconds });

// ── Query Reconciliation ───────────────────────────────────────────────────

export const queryReconciliationRequest = (
  params: ReconciliationQueryParams
): QueryReconciliationRequestAction =>
  createAction(QUERY_RECONCILIATION_REQUEST, params);

export const queryReconciliationSuccess = (
  data: ReconciliationQueryResponse,
  transactionType: TransactionTypeFilter
): QueryReconciliationSuccessAction =>
  createAction(QUERY_RECONCILIATION_SUCCESS, { ...data, transactionType });

export const queryReconciliationFailure =
  (): QueryReconciliationFailureAction =>
    createAction(QUERY_RECONCILIATION_FAILURE);

// ── Load Reconciliation Header ─────────────────────────────────────────────

export const loadReconciliationHeaderRequest = (
  connectionId: string,
  toDate: string
): LoadReconciliationHeaderRequestAction =>
  createAction(LOAD_RECONCILIATION_HEADER_REQUEST, { connectionId, toDate });

export const loadReconciliationHeaderSuccess = (
  headerData: ReconciliationHeaderData
): LoadReconciliationHeaderSuccessAction =>
  createAction(LOAD_RECONCILIATION_HEADER_SUCCESS, { headerData });

export const loadReconciliationHeaderFailure =
  (): LoadReconciliationHeaderFailureAction =>
    createAction(LOAD_RECONCILIATION_HEADER_FAILURE);

// ── Load Reconciliation Transactions ────────────────────────────────────────

export const loadReconciliationTransactionsRequest = (
  connectionId: string,
  mappingId: string,
  dataLogId: string,
  reportId: string
): LoadReconciliationTransactionsRequestAction =>
  createAction(LOAD_RECONCILIATION_TRANSACTIONS_REQUEST, {
    connectionId,
    mappingId,
    dataLogId,
    reportId,
  });

export const loadReconciliationTransactionsSuccess = (
  data: GenerateReconciliationDataResponse
): LoadReconciliationTransactionsSuccessAction =>
  createAction(LOAD_RECONCILIATION_TRANSACTIONS_SUCCESS, data);

export const loadReconciliationTransactionsFailure =
  (): LoadReconciliationTransactionsFailureAction =>
    createAction(LOAD_RECONCILIATION_TRANSACTIONS_FAILURE);

// ── View Saved Reconciliation Report ───────────────────────────────────────

export const viewReconciliationReportRequest = (
  params: ReconciliationViewParams
): ViewReconciliationReportRequestAction =>
  createAction(VIEW_RECONCILIATION_REPORT_REQUEST, params);

export const viewReconciliationReportPageSuccess = (
  data: ViewReconciliationDataResponse
): ViewReconciliationReportPageSuccessAction =>
  createAction(VIEW_RECONCILIATION_REPORT_PAGE_SUCCESS, data);

export const viewReconciliationReportComplete = (
  reportId?: string
): ViewReconciliationReportCompleteAction =>
  createAction(VIEW_RECONCILIATION_REPORT_COMPLETE, { reportId });

export const viewReconciliationReportFailure =
  (): ViewReconciliationReportFailureAction =>
    createAction(VIEW_RECONCILIATION_REPORT_FAILURE);

// ── Filters ────────────────────────────────────────────────────────────────

export const fetchReconciliationFiltersRequest = (
  connectionId: string,
  mappingId: string
): FetchReconciliationFiltersRequestAction =>
  createAction(FETCH_RECONCILIATION_FILTERS_REQUEST, {
    connectionId,
    mappingId,
  });

export const fetchReconciliationFiltersSuccess = (
  mappingCodes?: string[],
  accounts?: AccountFilter[],
  taxRates?: TaxRateFilter[]
): FetchReconciliationFiltersSuccessAction =>
  createAction(FETCH_RECONCILIATION_FILTERS_SUCCESS, {
    mappingCodes,
    accounts,
    taxRates,
  });

export const fetchReconciliationFiltersFailure =
  (): FetchReconciliationFiltersFailureAction =>
    createAction(FETCH_RECONCILIATION_FILTERS_FAILURE);

// ── Reconciliation Logs ────────────────────────────────────────────────────

export const fetchReconciliationLogsRequest = (
  organisationId: string,
  page?: number,
  pageSize?: number,
  filters?: Record<string, any>
): FetchReconciliationLogsRequestAction =>
  createAction(FETCH_RECONCILIATION_LOGS_REQUEST, {
    organisationId,
    page,
    pageSize,
    filters,
  });

export const fetchReconciliationLogsSuccess = (
  logs: ReconDataLog[],
  page: number,
  pageSize: number,
  totalElements: number,
  totalPages: number
): FetchReconciliationLogsSuccessAction =>
  createAction(FETCH_RECONCILIATION_LOGS_SUCCESS, {
    logs,
    page,
    pageSize,
    totalElements,
    totalPages,
  });

export const fetchReconciliationLogsFailure = (
  error?: string
): FetchReconciliationLogsFailureAction =>
  createAction(FETCH_RECONCILIATION_LOGS_FAILURE, { error });

// ── Save Reconciliation Report ──────────────────────────────────────────────

export const saveReconciliationReportRequest = (
  params: SaveReconciliationReportParams
): SaveReconciliationReportRequestAction =>
  createAction(SAVE_RECONCILIATION_REPORT_REQUEST, params);

export const saveReconciliationReportSuccess =
  (): SaveReconciliationReportSuccessAction =>
    createAction(SAVE_RECONCILIATION_REPORT_SUCCESS);

export const saveReconciliationReportFailure = (
  error?: string
): SaveReconciliationReportFailureAction =>
  createAction(SAVE_RECONCILIATION_REPORT_FAILURE, { error });

// ── Reset ──────────────────────────────────────────────────────────────────

export const resetReconciliation = (): ResetReconciliationAction =>
  createAction(RESET_RECONCILIATION);

// ── Update organisation tax balance b/f ─────────────────────────────────

export const updateOrganisationTaxBalanceRequest = (
  organisationId: string,
  taxBalance: number,
  refreshRecon?: {
    tokenId: string;
    toDate: string;
    mappingId?: string;
    asAtDate?: string;
    currency?: string;
  }
): UpdateOrganisationTaxBalanceRequestAction =>
  createAction(UPDATE_ORGANISATION_TAX_BALANCE_REQUEST, {
    organisationId,
    taxBalance,
    refreshRecon,
  });

export const updateOrganisationTaxBalanceSuccess =
  (): UpdateOrganisationTaxBalanceSuccessAction =>
    createAction(UPDATE_ORGANISATION_TAX_BALANCE_SUCCESS);

export const updateOrganisationTaxBalanceFailure = (
  error?: string
): UpdateOrganisationTaxBalanceFailureAction =>
  createAction(UPDATE_ORGANISATION_TAX_BALANCE_FAILURE, { error });
